import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  exportMasterWorkbook,
  type MasterExportKind,
  type MasterExportParams,
} from '@/lib/api-masters';

interface MasterExportButtonProps {
  kind: MasterExportKind;
  params?: MasterExportParams;
}

export const MasterExportButton = ({ kind, params = {} }: MasterExportButtonProps) => {
  const { t } = useTranslation('masters');
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportWorkbook = async () => {
    setIsExporting(true);
    setError(null);
    try {
      await exportMasterWorkbook(kind, params);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('efs.masters.export.error'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={() => void exportWorkbook()}
        disabled={isExporting}
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        {t('efs.masters.export.button')}
      </Button>
      {error ? (
        <Alert variant="destructive" className="max-w-xs py-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};
